import { useState } from 'react';
import { CalendarBlank, X } from '@phosphor-icons/react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

// DatePickerPopover — date-only picker with a button trigger and a
// Calendar popover. Use this instead of a raw `DateInputISO` whenever
// the surrounding UX is "click → pick", not "type a date". Stores and
// emits ISO YYYY-MM-DD just like DateInputISO so it's a drop-in swap
// at the API boundary.
//
// PopoverContent z-index is bumped to z-[70] so the popover renders
// above modal dialogs (which use z-[60] in this codebase). Sheets and
// drawers sit lower and are unaffected.
//
// `container` forwards to the Popover portal — pass the floating surface's
// content node (as with SelectContent's `container`) when this is mounted
// inside a modal Sheet/Dialog so the panel shares that surface's stacking
// context and stays interactive.
//
// `clearable` adds a small "clear" affordance below the trigger so the
// caller can return to the unselected ('') state — a plain Calendar has no
// way to un-pick. Only rendered when a value is set and the field is
// editable.

export interface DatePickerPopoverProps {
  value: string;                          // YYYY-MM-DD, '' for unselected
  onChange: (next: string) => void;
  locale: string;                         // BCP-47 for display + Calendar
  min?: string | undefined;               // YYYY-MM-DD, days before disabled
  max?: string | undefined;               // YYYY-MM-DD, days after disabled
  placeholder?: string;
  disabled?: boolean;
  // Snap the selected date — e.g. to an ISO Monday for week-start inputs.
  // Receives the YMD the user clicked, must return the YMD to store.
  snap?: (selected: string) => string;
  // Custom date display format. Default: locale `dateStyle: 'medium'`.
  format?: (value: string, locale: string) => string;
  // Portal target for the popover panel. See note above.
  container?: HTMLElement | null | undefined;
  // Show a "clear" button (needs clearLabel) that resets to ''.
  clearable?: boolean;
  clearLabel?: string;
  className?: string;
}

export function DatePickerPopover({
  value,
  onChange,
  locale,
  min,
  max,
  placeholder = '',
  disabled = false,
  snap,
  format,
  container,
  clearable = false,
  clearLabel,
  className,
}: DatePickerPopoverProps) {
  const [open, setOpen] = useState(false);
  const display = value
    ? (format ?? defaultFormat)(value, locale)
    : placeholder;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              'flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              value
                ? 'border-primary font-heading font-semibold text-foreground'
                : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground',
              disabled && 'cursor-not-allowed opacity-60 hover:border-border hover:text-muted-foreground',
            )}
          >
            <CalendarBlank className="h-3.5 w-3.5 shrink-0" weight="bold" />
            <span className="flex-1 text-left">{display || placeholder}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          side="bottom"
          sideOffset={4}
          className="z-[70] w-auto p-2"
          {...(container !== undefined ? { container } : {})}
        >
          <Calendar
            value={value || undefined}
            onSelect={(d) => {
              onChange(snap ? snap(d) : d);
              setOpen(false);
            }}
            {...(min !== undefined ? { min } : {})}
            {...(max !== undefined ? { max } : {})}
            locale={locale}
          />
        </PopoverContent>
      </Popover>
      {clearable && clearLabel && value && !disabled && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="self-start font-heading text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none"
        >
          <X className="mr-0.5 inline h-3 w-3" weight="bold" />
          {clearLabel}
        </button>
      )}
    </div>
  );
}

function defaultFormat(value: string, locale: string): string {
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return value;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(
    new Date(y, m - 1, d),
  );
}
